import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import ClientSearchPage from './pages/ClientSearchPage';
import PacketWizardPage from './pages/PacketWizardPage';
import PacketCompletePage from './pages/PacketCompletePage';
import PDFPreviewPage from './pages/PDFPreviewPage';
import AdminPage from './pages/AdminPage';
import HRDashboardPage from './pages/HRDashboardPage';
import LandingPage from './pages/LandingPage';
import EmploymentApplicationPage from './pages/EmploymentApplicationPage';
import EmploymentSuccessPage from './pages/EmploymentSuccessPage';
import BackgroundCheckPage from './pages/BackgroundCheckPage';
import EmploymentPortalPage from './pages/EmploymentPortalPage';
import './App.css';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="loading-spinner text-teal-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route wrapper (redirects to clients if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="loading-spinner text-teal-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/clients" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Simple URLs for public forms */}
      <Route path="/apply" element={<EmploymentApplicationPage />} />
      <Route path="/background" element={<BackgroundCheckPage />} />
      <Route path="/bid" element={<BackgroundCheckPage />} />
      
      {/* Employment Portal (Public) */}
      <Route path="/employment" element={<EmploymentPortalPage />} />
      <Route path="/employment/application" element={<EmploymentApplicationPage />} />
      <Route path="/employment/background-check" element={<BackgroundCheckPage />} />
      <Route path="/employment/success" element={<EmploymentSuccessPage />} />
      
      {/* Staff Login */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <ClientSearchPage />
          </ProtectedRoute>
        }
      />
      
      {/* Single packet completion flow - Wizard Style */}
      <Route
        path="/clients/:clientId/packet"
        element={
          <ProtectedRoute>
            <PacketWizardPage />
          </ProtectedRoute>
        }
      />
      
      {/* Packet complete / success page */}
      <Route
        path="/clients/:clientId/packet/complete"
        element={
          <ProtectedRoute>
            <PacketCompletePage />
          </ProtectedRoute>
        }
      />
      
      {/* PDF Preview page */}
      <Route
        path="/clients/:clientId/forms/:formId/preview"
        element={
          <ProtectedRoute>
            <PDFPreviewPage />
          </ProtectedRoute>
        }
      />
      
      {/* Admin page */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      
      {/* HR Dashboard */}
      <Route
        path="/admin/hr"
        element={
          <ProtectedRoute>
            <HRDashboardPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback for unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
